/**
 * Program kural motoru — saf. Girdi al, çıktı ver, yan etki yok.
 *
 * Tasarım kararı: haftalık program sabit bir ızgara DEĞİL.
 * Bir şablon (hangi gün normalde ne olur) + tek seferlik istisnalar (bugün ekstra tenis)
 * var; günün planı bu ikisinden TÜRETİLİR. Böylece "arkadaşlarla bugün tenise gidiyorum"
 * tek dokunuşla halloluyor ve öğün saatleri, hatırlatıcılar, antrenman sırası
 * kendiliğinden buna uyum sağlıyor.
 */

import { ACTIVITY_BY_KEY } from './energy.ts'

/**
 * Gün tipi. `sport` katalogdaki HERHANGİ bir aktivite olabilir — tenis burada
 * özel değil, `activityKey` ile belirtilen bir satır. Eskiden 'tennis' ve 'walk'
 * ayrı tiplerdi; bu, "cuma futbol oynadım" demeyi imkânsız kılıyordu.
 */
export type ActivityKind = 'sport' | 'strength' | 'prep' | 'rest'

/** Eski kayıtlar ('tennis', 'walk') yeni modele çevriliyor. */
export function normalizeKind(raw: string): { kind: ActivityKind; activityKey: string | null } {
  if (raw === 'tennis') return { kind: 'sport', activityKey: 'tennis_singles' }
  if (raw === 'walk') return { kind: 'sport', activityKey: 'walk_brisk' }
  if (raw === 'strength' || raw === 'prep' || raw === 'rest') return { kind: raw, activityKey: null }
  if (raw === 'sport') return { kind: 'sport', activityKey: null }
  return { kind: 'rest', activityKey: null }
}

/** Sporun adı — katalogdan. Bilinmeyen anahtar sessizce "Spor" olur. */
export function sportName(activityKey: string | null): string {
  return activityKey ? ACTIVITY_BY_KEY[activityKey]?.name ?? 'Spor' : 'Spor'
}

export interface TemplateDay {
  weekday: number // 0 = Pazar … 6 = Cumartesi (JS getDay ile aynı)
  kind: ActivityKind
  startTime: string | null // 'HH:mm' — sporun saati
  /** kind === 'sport' ise katalog anahtarı (tennis_singles, football_casual…) */
  activityKey?: string | null
  /** Süre; girilmezse ayarlardaki seans süresi kullanılıyor. */
  minutes?: number | null
}

export interface DayOverride {
  date: string // 'YYYY-MM-DD'
  kind: ActivityKind
  startTime: string | null
  activityKey?: string | null
  minutes?: number | null
  note?: string | null
}

export interface ScheduleSettings {
  strengthPerWeek: number // haftalık kuvvet antrenmanı hedefi
  makeupWeekday: number // telafi günü (6 = Cumartesi)
  makeupTime: string // 'HH:mm'
  preTennisMinutes: number // spor başlangıcından kaç dk önce ara öğün
  sessionMinutes: number // varsayılan spor süresi
  postTennisMinutes: number // spor bitiminden kaç dk sonra 2. öğün
}

export const DEFAULT_SETTINGS: ScheduleSettings = {
  strengthPerWeek: 3,
  makeupWeekday: 6,
  makeupTime: '10:00',
  preTennisMinutes: 150,
  sessionMinutes: 60,
  postTennisMinutes: 15,
}

export const DEFAULT_TEMPLATE: TemplateDay[] = [
  { weekday: 1, kind: 'strength', startTime: null, activityKey: null },
  { weekday: 2, kind: 'sport', startTime: '20:30', activityKey: 'tennis_singles' },
  { weekday: 3, kind: 'strength', startTime: null, activityKey: null },
  { weekday: 4, kind: 'sport', startTime: '20:30', activityKey: 'tennis_singles' },
  { weekday: 5, kind: 'strength', startTime: null, activityKey: null },
  { weekday: 6, kind: 'sport', startTime: null, activityKey: 'walk_brisk' },
  { weekday: 0, kind: 'prep', startTime: null, activityKey: null },
]

/** Pazartesi → Pazar. Hafta pazardan başlamıyor; tablo da öyle görünmemeli. */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0]

/**
 * Şablonu HER ZAMAN yedi güne tamamlar, Pazartesi'den Pazar'a sıralı.
 *
 * Sunucu varsayılan şablonu yalnızca üç gün için satır açıyordu (Pzt/Çrş/Cum
 * kuvvet). Ekrandaki tablo da "satırı olmayan günü çizme" diyordu, dolayısıyla
 * Salı/Perşembe/Cumartesi/Pazar tabloda HİÇ görünmüyordu — kullanıcı o günlere
 * bir şey atayamıyordu. "Haftalık programımı kendim dizayn edemiyorum"
 * şikayetinin tamamı buydu.
 *
 * Eksik gün `rest` sayılıyor: planForDate zaten öyle davranıyor, yani bu
 * uydurma bir varsayım değil, var olan davranışı görünür kılmak.
 */
export function fullTemplate(template: readonly TemplateDay[]): TemplateDay[] {
  return WEEKDAY_ORDER.map((weekday) =>
    template.find((t) => t.weekday === weekday)
    ?? { weekday, kind: 'rest' as ActivityKind, startTime: null, activityKey: null, minutes: null })
}

/**
 * Bir günü değiştirir ve yedi günlük tam şablonu döndürür.
 *
 * Eski hâli `template.map(...)` idi: şablonda olmayan bir günü değiştirmek
 * hiçbir şey yapmıyordu ve kaydetme sessizce boşa gidiyordu. Satır ekranda
 * görünse bile düğme çalışmazdı — o yüzden iki düzeltme birlikte lazım.
 *
 * Spor olmayan güne saat/aktivite/süre yazılmıyor: "dinlenme günü, 20:30,
 * tenis" gibi kendi içinde çelişen bir kayıt kalmasın.
 */
export function setTemplateDay(
  template: readonly TemplateDay[], weekday: number, patch: Partial<TemplateDay>,
): TemplateDay[] {
  return fullTemplate(template)
    .map((t) => (t.weekday === weekday ? { ...t, ...patch } : t))
    .map((t) => (t.kind === 'sport'
      ? t
      : { ...t, startTime: null, activityKey: null, minutes: null }))
}

export const WEEKDAY_NAMES = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi']

export const KIND_LABEL: Record<ActivityKind, string> = {
  sport: 'Spor',
  strength: 'Kuvvet antrenmanı',
  prep: 'Meal-prep',
  rest: 'Dinlenme',
}

// ── saat aritmetiği ────────────────────────────────────────────────────────
const toMin = (hhmm: string): number => {
  const [h, m] = hhmm.slice(0, 5).split(':').map(Number)
  return h * 60 + m
}
const toHHmm = (min: number): string => {
  const wrapped = ((min % 1440) + 1440) % 1440
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`
}
export const shift = (hhmm: string, minutes: number): string => toHHmm(toMin(hhmm) + minutes)

export const weekdayOf = (dateIso: string): number => new Date(dateIso + 'T00:00:00').getDay()

/**
 * Date → 'YYYY-MM-DD', YEREL güne göre.
 *
 * toISOString() KULLANMA: tarihleri yerel gece yarısı olarak kuruyoruz
 * (`new Date('2026-08-03T00:00:00')`), toISOString() ise UTC'ye çeviriyor.
 * Türkiye'de (UTC+3) yerel gece yarısı UTC'de bir önceki günün 21:00'i —
 * yani tüm hafta bir gün geriye kayıyordu. Bu hata UTC'de çalışırken
 * görünmüyor, sadece UTC+ saat dilimlerinde ortaya çıkıyor.
 */
const isoLocal = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** Haftanın Pazartesi'si. Hafta Pazartesi başlar — takvim değil, program mantığı. */
export function weekStart(dateIso: string): string {
  const d = new Date(dateIso + 'T00:00:00')
  const shiftBack = (d.getDay() + 6) % 7 // Pazartesi = 0
  d.setDate(d.getDate() - shiftBack)
  return isoLocal(d)
}

export function addDays(dateIso: string, n: number): string {
  const d = new Date(dateIso + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return isoLocal(d)
}

export interface MealTimes {
  meal1: string
  preSnack: string | null
  meal2: string
  kitchenClose: string
}

export interface DayPlan {
  date: string
  weekday: number
  kind: ActivityKind
  /** Şablonda ne yazıyordu — istisna varsa buradan görünür. */
  plannedKind: ActivityKind
  isOverride: boolean
  /** Şablonda kuvvet vardı ama tenis geldi → o antrenman telafiye düştü. */
  displacedStrength: boolean
  startTime: string | null
  /** kind === 'sport' ise hangi aktivite (katalog anahtarı). */
  activityKey: string | null
  /** Sporun süresi — enerji hesabına ve öğün kaymasına girer. */
  minutes: number
  /** Kuvvet gününde sıradaki antrenman. Takvimden değil, geçmişten türetilir. */
  workout: string | null
  meals: MealTimes
  label: string
  detail: string
}

export interface BaseMealTimes {
  meal1: string
  meal2: string
  kitchenClose: string
}

/**
 * A/B rotasyonu **tamamlanan antrenman sayısından** türetilir, takvimden değil.
 * Takvime bağlarsan bir antrenmanı atladığında sıra kayar ve iki hafta üst üste
 * aynı antrenmanı yaparsın.
 */
export function nextWorkout(completedStrengthTotal: number, templateCount = 2): string {
  const n = Math.max(1, templateCount)
  return ['A', 'B', 'C', 'D', 'E'][completedStrengthTotal % n]
}

export function planForDate(
  dateIso: string,
  template: TemplateDay[],
  overrides: DayOverride[],
  base: BaseMealTimes,
  completedStrengthTotal: number,
  settings: ScheduleSettings = DEFAULT_SETTINGS,
): DayPlan {
  const weekday = weekdayOf(dateIso)
  const tpl = template.find((t) => t.weekday === weekday)
  const plannedKind: ActivityKind = tpl?.kind ?? 'rest'
  const ov = overrides.find((o) => o.date === dateIso)

  const kind = ov?.kind ?? plannedKind
  const startTime = ov?.startTime ?? (ov ? null : tpl?.startTime ?? null)
  const activityKey = (ov ? ov.activityKey : tpl?.activityKey) ?? null
  const minutes = (ov ? ov.minutes : tpl?.minutes) ?? settings.sessionMinutes

  const displacedStrength = !!ov && plannedKind === 'strength' && ov.kind !== 'strength'

  // Öğün saatleri: tenis günü pencere kayar, diğer günler sabit.
  let meals: MealTimes = {
    meal1: base.meal1.slice(0, 5),
    preSnack: null,
    meal2: base.meal2.slice(0, 5),
    kitchenClose: base.kitchenClose.slice(0, 5),
  }

  // Öğün penceresi akşam sporu olan HER günde kayıyor — tenise özel değil.
  if (kind === 'sport' && startTime) {
    const meal2 = shift(startTime, minutes + settings.postTennisMinutes)
    meals = {
      meal1: base.meal1.slice(0, 5),
      preSnack: shift(startTime, -settings.preTennisMinutes),
      meal2,
      kitchenClose: shift(meal2, 45),
    }
  }

  const workout = kind === 'strength' ? nextWorkout(completedStrengthTotal) : null

  return {
    date: dateIso,
    weekday,
    kind,
    plannedKind,
    isOverride: !!ov,
    displacedStrength,
    startTime,
    workout,
    meals,
    activityKey,
    minutes,
    label: buildLabel(kind, workout, startTime, activityKey),
    detail: buildDetail(kind, meals, displacedStrength, settings),
  }
}

function buildLabel(
  kind: ActivityKind, workout: string | null, startTime: string | null,
  activityKey: string | null,
): string {
  if (kind === 'sport') {
    const name = sportName(activityKey)
    return startTime ? `${name} ${startTime}` : name
  }
  if (kind === 'strength') return `Evde — Antrenman ${workout ?? 'A'}`
  if (kind === 'prep') return 'Meal-prep + dondurucu stoğu'
  return 'Dinlenme'
}

function buildDetail(
  kind: ActivityKind,
  meals: MealTimes,
  displaced: boolean,
  s: ScheduleSettings,
): string {
  const parts: string[] = []

  if (kind === 'sport') {
    if (meals.preSnack) parts.push(`${meals.preSnack} küçük ara öğün (muz + yoğurt, ~200 kcal)`)
    if (meals.preSnack) parts.push(`${meals.meal2} 2. öğün — protein ağırlıklı, hafif`)
    else parts.push(`Öğünler ${meals.meal1} ve ${meals.meal2}`)
    if (meals.preSnack) parts.push('Pilav/makarna/pide yok')
  } else if (kind === 'strength') {
    parts.push('25–30 dk, direnç bandıyla')
    parts.push(`Öğünler ${meals.meal1} ve ${meals.meal2}`)
  } else if (kind === 'prep') {
    parts.push('60–90 dk mutfak · 2 porsiyon fazladan pişir ve dondur')
  } else {
    parts.push('En az 30 dk yürüyüş')
  }

  if (displaced) {
    parts.push(`Bugünkü kuvvet antrenmanı ${WEEKDAY_NAMES[s.makeupWeekday]} ${s.makeupTime}'a telafiye düştü`)
  }
  return parts.join(' · ')
}

export interface WeekSummary {
  weekStart: string
  strengthTarget: number
  strengthPlanned: number
  strengthCompleted: number
  strengthOwed: number
  sportPlanned: number
  makeupDate: string | null
  makeupTime: string
  /** Telafi günü de dolduysa hedefe bu hafta ulaşılamaz — dürüst ol, sessizce yutma. */
  unreachable: boolean
}

/**
 * Haftalık özet. Asıl fikir: sabit gün değil, **haftalık kota**.
 * Bir kuvvet günü teniste kaybolursa hedef kaybolmuyor, telafiye kayıyor.
 */
export function weekSummary(
  dateIso: string,
  template: TemplateDay[],
  overrides: DayOverride[],
  completedDates: string[],
  settings: ScheduleSettings = DEFAULT_SETTINGS,
): WeekSummary {
  const start = weekStart(dateIso)
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i))

  let strengthPlanned = 0
  let sportPlanned = 0
  for (const d of days) {
    const weekday = weekdayOf(d)
    const ov = overrides.find((o) => o.date === d)
    const kind = ov?.kind ?? template.find((t) => t.weekday === weekday)?.kind ?? 'rest'
    if (kind === 'strength') strengthPlanned++
    if (kind === 'sport') sportPlanned++
  }

  const strengthCompleted = completedDates.filter((d) => d >= start && d <= days[6]).length
  const target = settings.strengthPerWeek

  // Hedefe kalan seans sayısı. strengthPlanned'ı buna EKLEME —
  // planlanan günler tamamlananları zaten kapsıyor, toplamak çift sayım olur.
  const owed = Math.max(0, target - strengthCompleted)

  // Programın kapasitesi hedefin altındaysa telafi gerekiyor demektir.
  const shortfall = target - strengthPlanned

  // Telafi günü: o günde kuvvet planlı değilse orası boş demektir.
  const makeupDate = days.find((d) => weekdayOf(d) === settings.makeupWeekday) ?? null
  const makeupFree =
    makeupDate !== null &&
    (overrides.find((o) => o.date === makeupDate)?.kind ??
      template.find((t) => t.weekday === settings.makeupWeekday)?.kind) !== 'strength'

  return {
    weekStart: start,
    strengthTarget: target,
    strengthPlanned,
    strengthCompleted,
    strengthOwed: owed,
    sportPlanned,
    makeupDate: shortfall > 0 && makeupFree ? makeupDate : null,
    makeupTime: settings.makeupTime,
    unreachable: shortfall > 0 && !makeupFree,
  }
}

/**
 * Sunucunun o gün göndermesi gereken hatırlatıcılar.
 * Backend'deki ReminderService bu listeyi üretir; istemci aynı fonksiyonu
 * önizleme için kullanır — tek kaynak, iki tüketici.
 */
export interface ReminderSpec {
  kind: string
  at: string
  title: string
  body: string
}

export function remindersFor(plan: DayPlan): ReminderSpec[] {
  const out: ReminderSpec[] = [
    {
      kind: 'meal1',
      at: plan.meals.meal1,
      title: '1. öğün vakti',
      body: 'Önce çorba ya da salata. Ana tabak sonra — ekran kapalı, masadan kalkarak.',
    },
  ]

  if (plan.meals.preSnack) {
    out.push({
      kind: 'presnack',
      at: plan.meals.preSnack,
      title: 'Tenis öncesi ara öğün',
      body: '1 muz + 1 kase yoğurt (~200 kcal). Aç antrenmana gitme, 20:30\'da bacakların gider.',
    })
  }

  out.push({
    kind: 'meal2',
    at: plan.meals.meal2,
    title: '2. öğün vakti',
    body:
      plan.kind === 'sport'
        ? 'Kort dönüşü: protein ağırlıklı ve hafif. Pilav/makarna yok.'
        : 'Yine önce çorba/salata. Bu öğünde karbonhidrat daha az.',
  })

  out.push({
    kind: 'kitchen',
    at: plan.meals.kitchenClose,
    title: 'Mutfak kapanıyor',
    body: 'Pencere kapandı. Su ve sade çay serbest.',
  })

  return out.sort((a, b) => (a.at < b.at ? -1 : 1))
}
